import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Calendar } from 'lucide-react';
import CalendarEventForm from './CalendarEventForm';

export default function CalendarEventsList({
  events,
  loading,
  error,
  canManage,
  onCreate,
  onUpdate,
  onDelete
}) {
  const [showForm, setShowForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filter, setFilter] = useState('');

  const filteredEvents = events.filter(event =>
    !filter || event.tipo === filter
  );

  const tipoColors = {
    feriado_nacional: 'bg-red-100 text-red-700 border-red-300',
    feriado_regional: 'bg-orange-100 text-orange-700 border-orange-300',
    feriado_trasladable: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    no_laborable_empresa: 'bg-purple-100 text-purple-700 border-purple-300',
    medio_dia: 'bg-blue-100 text-blue-700 border-blue-300',
    evento_corporativo: 'bg-green-100 text-green-700 border-green-300'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header con filtros */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos los tipos</option>
          <option value="feriado_nacional">Feriado Nacional</option>
          <option value="feriado_regional">Feriado Regional</option>
          <option value="feriado_trasladable">Feriado Trasladable</option>
          <option value="no_laborable_empresa">No Laboral Empresa</option>
          <option value="medio_dia">Medio Día</option>
          <option value="evento_corporativo">Evento Corporativo</option>
        </select>

        {canManage && (
          <button
            onClick={() => {
              setSelectedEvent(null);
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Nuevo Evento
          </button>
        )}
      </div>

      {/* Lista de eventos */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No hay eventos registrados</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              className={`border-l-4 rounded-lg p-4 ${
                tipoColors[event.tipo] || 'bg-gray-100 text-gray-700 border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">{event.nombre}</h3>
                  <p className="text-sm opacity-80 mb-2">{event.descripcion}</p>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(event.fecha.split('T')[0] + 'T12:00:00').toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}</span>
                    </div>
                    {!event.es_laborable && (
                      <span className="px-2 py-1 bg-white/50 rounded text-xs font-medium">
                        No Laboral
                      </span>
                    )}
                    {event.pago_especial && (
                      <span className="px-2 py-1 bg-white/50 rounded text-xs font-medium">
                        Pago Especial (x{event.multiplicador_pago})
                      </span>
                    )}
                  </div>
                </div>

                {canManage && (
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => {
                        setSelectedEvent(event);
                        setShowForm(true);
                      }}
                      className="p-2 hover:bg-white/30 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('¿Eliminar este evento?')) {
                          onDelete(event.id);
                        }
                      }}
                      className="p-2 hover:bg-white/30 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de formulario */}
      {showForm && (
        <CalendarEventForm
          event={selectedEvent}
          onSubmit={selectedEvent ? onUpdate : onCreate}
          onClose={() => {
            setShowForm(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
}
